import { Repeat } from "lucide-react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import {
  RefinanceOfferTerms,
  type OriginationInput,
} from "@/components/application/merchant-tasks/config/OfferTerms";
import {
  formatCurrency,
  formatPercentPoints,
  normalizeOriginationFeePct,
  parseDecimalLike,
} from "@/components/application/merchant-tasks/originationFee";
import { useOrganizationSettingsQuery } from "@/hooks/queries";
import { underwritingRuleValue } from "@/services/organizationSettings";
import { resolveBusinessDaysPerMonth, roundOfferFactor } from "@/utils/offerCalculations";
import {
  paymentFromRefiAmount,
  refiAmountFromPayment,
  refinanceBalanceError,
  refinanceDisbursed,
  refinancePayback,
  relinkRefinanceValues,
  type RefinanceCalcValues,
  type RefinanceDriver,
} from "@/utils/refinanceOfferCalc";

import type { ToolArtifact } from "../../aui/convert";
import type { WireChoice } from "../../protocol/protocol";
import type { AgentState } from "../../store/agentSlice";
import { selectChoice } from "../../store/selectors";
import type { NativeCard, NativeCardProps } from "..";
import {
  CardFooter,
  CardShell,
  CHANGE_HINT,
  ChangeHint,
  EDIT_HINT,
  ReceiptLine,
  StripSection,
  TermChanges,
} from "./CardParts";
import { ChoiceButtons, GenericApproval } from "./ChoiceParts";
import { refinanceFollows, refinanceOverrides, useCardEdits, type Overrides } from "./overrides";
import {
  APPROVAL_CARD_VERSION,
  isRefinanceApprovalCard,
  isRefinancePayload,
  money,
  num,
  termChanges,
  type AnalysisStrip,
  type Decimal,
  type RefinanceApprovalCard,
  type RefinanceCosts,
  type RefinanceTerms,
} from "./payloads";

/** What the card draws: the refinance offer read or saved, or the one a save proposes. */
interface RefinanceView {
  applicationId: number;
  /** The application's status, as the header's pill (an older approval card carries none). */
  status: string | null;
  eligible: boolean;
  offer: RefinanceTerms | null;
  costs: RefinanceCosts | null;
  /** The approved offer's added leverage; unknown on an approval card. */
  specifiedPercentage: Decimal;
  strip: AnalysisStrip | null;
}

/** The offer's terms as the refinance math holds them. */
const calcValues = (offer: RefinanceTerms): RefinanceCalcValues => ({
  refiAmount: num(offer.refi_amount) ?? 0,
  factor: num(offer.factor) ?? 0,
  payment: num(offer.merchant_installment) ?? 0,
  balance: num(offer.balance) ?? 0,
  term: offer.term ?? 0,
  frequency: offer.frequency,
});

/** What an editable refinance section reports to its card's edits. */
interface RefinanceEdit {
  /** The arguments the card lets the user change. */
  editable: ReadonlySet<string>;
  report: (values: Overrides, follows: string[]) => void;
}

/**
 * The refinance Offer section the user may change, as Merchant Tasks edits it: the funding
 * amount and the payment worked out from each other over factor, term and frequency, payback,
 * disbursed and the fee from what is typed. Each change is reported to the card's edits.
 */
function EditableTerms({
  offer,
  edit,
  specifiedPercentage,
}: {
  offer: RefinanceTerms;
  edit: RefinanceEdit;
  specifiedPercentage: string | null;
}) {
  const { data: settings } = useOrganizationSettingsQuery(true);
  const businessDaysPerMonth = resolveBusinessDaysPerMonth(
    underwritingRuleValue(settings, "business_days_per_month")
  );
  const [values, setValues] = useState(() => calcValues(offer));
  const [lastEdited, setLastEdited] = useState<RefinanceDriver | null>(null);
  // Factor, term or frequency typed in: the payment follows the funding amount from then on.
  const [termsTouched, setTermsTouched] = useState(false);
  const [pct, setPct] = useState(() => num(offer.origination_fee_percentage) ?? 0);
  const [pctInput, setPctInput] = useState(() => formatPercentPoints(pct));
  const { report } = edit;

  useEffect(() => {
    report(refinanceOverrides(values, pct), refinanceFollows(lastEdited));
  }, [values, pct, lastEdited, report]);

  useEffect(() => {
    setValues((prev) =>
      relinkRefinanceValues(prev, lastEdited, termsTouched, businessDaysPerMonth)
    );
  }, [
    values.factor,
    values.term,
    values.frequency,
    lastEdited,
    termsTouched,
    businessDaysPerMonth,
  ]);

  const term = (patch: Partial<RefinanceCalcValues>) => {
    setTermsTouched(true);
    setValues((prev) => ({ ...prev, ...patch }));
  };
  const applyPct = (next: number) => {
    const clamped = normalizeOriginationFeePct(next);
    setPct(clamped);
    setPctInput(formatPercentPoints(clamped));
  };
  const origination: OriginationInput | number = edit.editable.has("origination_fee_percentage")
    ? {
        pct,
        input: pctInput,
        onInput: (raw) => {
          setPctInput(raw);
          const next = parseDecimalLike(raw);
          if (next != null) setPct(normalizeOriginationFeePct(next));
        },
        onStep: (delta) => applyPct(pct + delta),
      }
    : pct;
  const { refiAmount, balance, factor } = values;

  return (
    <RefinanceOfferTerms
      layout="auto"
      values={values}
      payback={refiAmount > 0 ? formatCurrency(refinancePayback(refiAmount, factor)) : ""}
      disbursed={refiAmount > 0 ? formatCurrency(refinanceDisbursed(refiAmount, balance, pct)) : ""}
      specifiedPercentage={specifiedPercentage}
      originationFee={refiAmount > 0 ? formatCurrency((refiAmount * pct) / 100) : ""}
      origination={origination}
      balanceError={refinanceBalanceError(balance, refiAmount, pct)}
      edit={{
        onFunding: (amount) => {
          setLastEdited("refiAmount");
          setValues((prev) => ({
            ...prev,
            refiAmount: amount,
            payment: paymentFromRefiAmount(
              amount,
              prev.factor,
              prev.term,
              prev.frequency,
              businessDaysPerMonth
            ),
          }));
        },
        onPayment: (payment) => {
          setLastEdited("payment");
          setValues((prev) => ({
            ...prev,
            payment,
            refiAmount: refiAmountFromPayment(
              payment,
              prev.factor,
              prev.term,
              prev.frequency,
              businessDaysPerMonth
            ),
          }));
        },
        onFactor: (n) => term({ factor: roundOfferFactor(n) || 1.49 }),
        onBalance: (n) => setValues((prev) => ({ ...prev, balance: n })),
        onFrequency: (frequency) => term({ frequency }),
        onTerm: (n) => term({ term: n === 0 ? 0 : Math.max(1, n || 60) }),
      }}
    />
  );
}

/**
 * The Merchant Tasks send card's refinance Offer section: read-only, or the user's to change
 * (`edit`, a waiting save whose card lists `overrides`).
 */
function Terms({ view, edit }: { view: RefinanceView; edit?: RefinanceEdit & { key: string } }) {
  const { offer, costs } = view;
  if (!offer) {
    return (
      // The section's top rule is the padding above an offer; without one, the Offer card's.
      <p className="pt-3 text-sm text-muted-foreground">
        {view.eligible
          ? "No refinance offer yet."
          : "This application can't be refinanced in its current status."}
      </p>
    );
  }
  const specified = num(view.specifiedPercentage);
  // Unknown (a save's approval card has none): the row is left out rather than read "-".
  const specifiedPercentage = specified == null ? null : `${formatPercentPoints(specified)}%`;
  if (edit) {
    return (
      <EditableTerms
        key={edit.key}
        offer={offer}
        edit={edit}
        specifiedPercentage={specifiedPercentage}
      />
    );
  }
  return (
    <RefinanceOfferTerms
      layout="auto"
      values={calcValues(offer)}
      payback={money(costs?.payback)}
      disbursed={money(costs?.disbursed)}
      specifiedPercentage={specifiedPercentage}
      originationFee={money(costs?.origination_fee)}
      origination={num(offer.origination_fee_percentage) ?? 0}
    />
  );
}

function Refinance({
  view,
  approval,
  choice,
  artifact,
}: {
  view: RefinanceView;
  /** The save's approval card: what it changes, or that the offer is new. */
  approval: RefinanceApprovalCard | null;
  choice: WireChoice | null;
  artifact: ToolArtifact;
}) {
  const pending = choice?.status === "pending";
  const { editable, report, edits } = useCardEdits(approval ? choice : null);
  const edit =
    pending && editable.size ? { editable, report, key: choice?.args_sha256 ?? "" } : undefined;
  return (
    <CardShell
      icon={Repeat}
      title="Refinance offer"
      status={view.status}
      data-state={pending ? "pending" : artifact.state}
    >
      <StripSection strip={view.strip} />
      {/* The section's own top rule stands in for a top padding. */}
      <div className="border-b px-3.5 pb-3 [&>section]:border-t-0">
        <Terms view={view} edit={edit} />
        {approval && (
          <TermChanges
            changes={termChanges(approval)}
            fresh={approval.current ? undefined : "A new refinance offer; none to replace."}
          />
        )}
        {pending && <ChangeHint>{edit ? EDIT_HINT : CHANGE_HINT}</ChangeHint>}
      </div>
      <CardFooter applicationId={view.applicationId}>
        {pending ? (
          // A press that redraws the card says so beside these buttons.
          <ChoiceButtons choice={choice} edits={edits} />
        ) : artifact.state === "failed" ? (
          <p className="min-w-0 text-xs font-medium text-destructive">{artifact.line}</p>
        ) : choice ? (
          <ReceiptLine choice={choice} done="Saved" notDone="Not saved" />
        ) : null}
      </CardFooter>
    </CardShell>
  );
}

/**
 * get_refinance_offer: the refinance offer as the Merchant Tasks send card shows it, under the
 * strip. propose_refinance_offer: the same at the offer it would save (its approval card), with
 * what it changes and Save refinance offer / Don't save, then its receipt. Sending the refinance
 * contract stays in Merchant Tasks.
 */
function RefinanceCard({ result, artifact }: NativeCardProps) {
  const choice = useSelector((state: { agent: AgentState }) =>
    artifact.choiceRef ? selectChoice(state, artifact.choiceRef) : null
  );
  const approval = isRefinanceApprovalCard(choice?.card) ? choice.card : null;
  let view: RefinanceView | null = null;
  if (artifact.state === "success" && isRefinancePayload(result)) {
    view = {
      applicationId: result.application_id,
      status: result.app_status,
      eligible: result.eligible,
      offer: result.offer,
      costs: result.costs,
      specifiedPercentage: result.specified_percentage,
      strip: result.strip,
    };
  } else if (approval) {
    view = {
      applicationId: approval.application.id,
      // Until it is saved, the status it is in.
      status: approval.status?.from ?? null,
      // The tool takes a save only where a refinance is offered.
      eligible: true,
      offer: approval.terms,
      costs: approval.costs,
      specifiedPercentage: null,
      strip: approval.strip,
    };
  }
  if (!view) return <GenericApproval artifact={artifact} />;
  return <Refinance view={view} approval={approval} choice={choice} artifact={artifact} />;
}

export const REFINANCE_CARD: NativeCard = {
  Card: RefinanceCard,
  // A save with no approval card (an older message) or one of another version keeps its generic
  // card.
  shows: (artifact, result) =>
    (artifact.state === "success" && isRefinancePayload(result)) ||
    (artifact.choiceCardKind === "refinance" &&
      artifact.choiceCardVersion === APPROVAL_CARD_VERSION),
};
